import { Component, Input, OnInit } from '@angular/core';
import { AlertService } from '../../shared/alert/alert.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Germplasm } from '../../entities/germplasm/germplasm.model';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { GermplasmSearchRequest } from '../../entities/germplasm/germplasm-search-request.model';
import { GermplasmMergeRequest, MergeOptions, NonSelectedGermplasm } from '../../shared/germplasm/model/germplasm-merge-request.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { MergeGermplasmExistingLotsComponent } from './merge-germplasm-existing-lots.component';
import { MergeGermplasmReviewComponent } from './merge-germplasm-review.component';
import { VIEW_NAMES_PERMISSION, VIEW_PEDIGREE_INFORMATION_PERMISSION } from '../../shared/auth/permissions';
import { Principal } from '../../shared';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { InventoryDetailsPopupService } from '../inventory/details/inventory-details-popup.service';
import { GermplasmDetailsUrlService } from '../../shared/germplasm/service/germplasm-details.url.service';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';

@Component({
    selector: 'jhi-merge-germplasm-select',
    templateUrl: './merge-germplasm-selection.component.html',
})
export class MergeGermplasmSelectionComponent implements OnInit {

    VIEW_PEDIGREE_INFORMATION_PERMISSION = VIEW_PEDIGREE_INFORMATION_PERMISSION;
    VIEW_NAMES_PERMISSION = VIEW_NAMES_PERMISSION;
    @Input()
        gids: number[];

    isLoading: boolean;
    germplasmList: Germplasm[];
    request = new GermplasmSearchRequest();
    searchResultDbId: string;

    selectedGid: number;
    isTransferPassportData: boolean;
    isTransferAttributesData: boolean;
    isTransferNameTypesData: boolean;
    isTransferFiles: boolean;
    excludedGermplasmGIDs: any[] = [];

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;

    constructor(
        private alertService: AlertService,
        private modal: NgbActiveModal,
        private modalService: NgbModal,
        private germplasmService: GermplasmService,
        private principal: Principal,
        protected germplasmDetailsPopupService: GermplasmDetailsPopupService,
        protected inventoryDetailsPopupService: InventoryDetailsPopupService,
        protected germplasmDetailsUrlService: GermplasmDetailsUrlService
    ) {
    }

    ngOnInit(): void {
        this.request = new GermplasmSearchRequest();
        this.request.addedColumnsPropertyIds = ['PREFERRED NAME', 'HAS PROGENY', 'FGID', 'MGID'];
        this.request.gids = this.gids;

        this.isLoading = true;
        this.search(this.request).then((searchId) => {
            this.germplasmService.getSearchResults(
                { searchRequestId: searchId }
            ).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<Germplasm[]>) => this.germplasmList = res.body,
            );
        }, () => this.isLoading = false);

    }

    search(request: GermplasmSearchRequest): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!this.searchResultDbId) {
                this.germplasmService.search(request).subscribe((response) => {
                    this.searchResultDbId = response;
                    resolve(this.searchResultDbId);
                }, (error) => reject(error));
            } else {
                resolve(this.searchResultDbId);
            }
        });
    }

    isSelected(germplasm: Germplasm) {
        return this.selectedGid === germplasm.gid;
    }

    toggleSelect(germplasm: Germplasm) {
        if (this.selectedGid !== germplasm.gid) {
            this.selectedGid = germplasm.gid;
            if (this.excludedGermplasmGIDs.length > 0 && this.excludedGermplasmGIDs.find((excludedGID) => excludedGID === germplasm.gid)) {
                this.excludedGermplasmGIDs = this.excludedGermplasmGIDs.filter((item) => item !== germplasm.gid);
            }
        }
    }

    isExcluded(germplasm: Germplasm) {
        return germplasm && this.excludedGermplasmGIDs.length > 0 && this.excludedGermplasmGIDs.find((excludedGID) => excludedGID === germplasm.gid);
    }

    toggleExclude(germplasm: Germplasm) {
        if (this.selectedGid !== germplasm.gid) {
            if (this.excludedGermplasmGIDs.length > 0 && this.excludedGermplasmGIDs.find((excludedGID) => excludedGID === germplasm.gid)) {
                this.excludedGermplasmGIDs = this.excludedGermplasmGIDs.filter((item) => item !== germplasm.gid);
            } else {
                this.excludedGermplasmGIDs.push(germplasm.gid);
            }
        }
    }

    dismiss() {
        this.modal.dismiss();
    }

    confirm() {
        this.isLoading = true;
        const nonSelectedGermplasmList = this.germplasmList
            .filter((germplasm) => this.selectedGid !== germplasm.gid && !this.excludedGermplasmGIDs.includes(germplasm.gid));
        if (this.hasValidationError(nonSelectedGermplasmList)) {
            this.isLoading = false;
            return;
        }
        const germplasmMergeRequest = new GermplasmMergeRequest();
        germplasmMergeRequest.mergeOptions = new MergeOptions();
        germplasmMergeRequest.mergeOptions.migrateAttributesData = this.isTransferAttributesData;
        germplasmMergeRequest.mergeOptions.migrateNameTypes = this.isTransferNameTypesData;
        germplasmMergeRequest.mergeOptions.migratePassportData = this.isTransferPassportData;
        germplasmMergeRequest.mergeOptions.migrateFiles = this.isTransferFiles;
        germplasmMergeRequest.targetGermplasmId = this.selectedGid;
        germplasmMergeRequest.nonSelectedGermplasm = [];

        nonSelectedGermplasmList.forEach((germplasm) => {
            const nonSelectedGermplasm = new NonSelectedGermplasm();
            nonSelectedGermplasm.germplasm = germplasm;
            nonSelectedGermplasm.germplasmId = germplasm.gid;
            nonSelectedGermplasm.migrateLots = false;
            nonSelectedGermplasm.omit = false;
            germplasmMergeRequest.nonSelectedGermplasm.push(nonSelectedGermplasm);
        });

        this.germplasmService.validateHasNoAncestralRelation(germplasmMergeRequest).subscribe((res: void) => {
            const gidsWithLots = nonSelectedGermplasmList.filter((germplasm) => (germplasm.lotCount > 0)).map((germplasm) => germplasm.gid);

            if (gidsWithLots.length > 0) {
                const mergeGermplasmExistingLotsModal =
                    this.modalService.open(MergeGermplasmExistingLotsComponent as Component, { modalDialogClass: 'modal-autofit', backdrop: 'static' });
                mergeGermplasmExistingLotsModal.componentInstance.gidsWithLots = gidsWithLots;
                mergeGermplasmExistingLotsModal.componentInstance.germplasmMergeRequest = germplasmMergeRequest;
                this.modal.dismiss();
            } else {
                const mergeGermplasmReviewModal = this.modalService.open(MergeGermplasmReviewComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
                mergeGermplasmReviewModal.componentInstance.germplasmMergeRequest = germplasmMergeRequest;
                this.modal.dismiss();
            }
        }, () => this.isLoading = false);
    }

    showCrossColumn() {
        return this.principal.hasAnyAuthorityDirect(VIEW_PEDIGREE_INFORMATION_PERMISSION) && this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION);
    }

    private hasValidationError(nonSelectedGermplasmList: Germplasm[]): boolean {
        if (nonSelectedGermplasmList && nonSelectedGermplasmList.length === 0) {
            this.alertService.error('merge-germplasm.non-selected-germplasm-all-excluded');
            return true;
        }
        const fixedGids = nonSelectedGermplasmList.filter((germplasm) => (germplasm.groupId !== 0)).map((germplasm) => germplasm.gid);
        if (fixedGids.length > 0) {
            this.alertService.error('merge-germplasm.non-selected-germplasm-fixed', { param: fixedGids.join() });
            return true;
        }
        return false;
    }

    private getGidsToDisplay(gids: number[]): string {
        return gids. length > 7 ? gids.slice(0, 7).join() + '...' : gids.join();
    }

    trackId(germplasm: Germplasm) {
        return germplasm.gid;
    }

    openGermplasmDetailsInTab(gid: number) {
        const url = this.germplasmDetailsUrlService.getUrlAsString(gid);
        window.open(url, '_blank');
    }

}
